import { AppColorPickerOnExecuteParameter, AppColorPickerProps } from '@1js/acui-color-picker';
import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import {
    createAppButton,
    createAppSplitButton,
    getKeytipString,
    constructKeytip,
} from 'owa-command-ribbon';
import { lazySetLastTextColor } from 'owa-editor-ribbonplugin-store';
import type RibbonFormatState from 'owa-editor-ribbonplugin-store/lib/store/schema/RibbonFormatState';
import { callFormatApi } from 'owa-editor/lib/utils/callFormatApi';
import { getComposeRibbonId } from 'owa-compose-ribbon-utils/lib/utils/getComposeRibbonId';
import MoreColor from 'owa-fluent-icons-svg/lib/icons/ColorRegular';
import loc from 'owa-localize';
import { FormatControlId } from 'owa-ribbon-ids';
import { startLivePreview, stopLivePreview } from '../util/livePreviewHandler';
import { getTextColorCells, getTextColorCode } from './ColorPickerCellColors';
import showCustomColorPicker from './CustomColorPicker';
import { fontColorText, moreColorsText } from './formatControlStrings.locstring.json';
import TextColorPickerIcon from './TextColorPickerIcon';
import { isFeatureEnabled } from 'owa-feature-flags';
import { callEditorApi } from 'owa-editor/lib/utils/callEditorApi';

export const getFontColorPickerControl = computedFn(
    (
        editorId: string,
        formatViewState: RibbonFormatState,
        tabId: number | undefined
    ): RibbonControlProps => {
        const lastTextColors = formatViewState.lastTextColors;
        const lightModeColor = formatViewState.lastTextColors.lightModeColor;
        return createAppSplitButton(
            getComposeRibbonId(FormatControlId.FontColorPicker, editorId),
            loc(fontColorText),
            {
                sections: [
                    {
                        title: loc(fontColorText),
                        controls: [
                            <AppColorPickerProps>{
                                id: getComposeRibbonId('fontColorPicker', editorId),
                                type: 'AppColorPickerProps',
                                columnCount: 6,
                                colorCells: getTextColorCells(),
                                onExecute: (colorItem: AppColorPickerOnExecuteParameter) => {
                                    if (colorItem) {
                                        stopLivePreview(editorId);
                                        const colorCode = getTextColorCode(
                                            colorItem.selectedColorId as string
                                        );
                                        callFormatApi(
                                            editorId,
                                            'setTextColor',
                                            [colorCode],
                                            [colorCode.lightModeColor]
                                        );
                                        lazySetLastTextColor.importAndExecute(
                                            formatViewState,
                                            colorCode
                                        );
                                    }
                                },
                                onFocus: (colorItem: AppColorPickerOnExecuteParameter) => {
                                    startLivePreview(editorId, () => {
                                        const colorCode = getTextColorCode(
                                            colorItem.selectedColorId as string
                                        );
                                        callFormatApi(editorId, 'setTextColor', [colorCode]);
                                    });
                                },
                            },
                        ],
                    },
                    {
                        controls: [
                            createAppButton(
                                getComposeRibbonId(FormatControlId.MoreColor, editorId),
                                loc(moreColorsText),
                                () => {
                                    const targetWindow =
                                        callEditorApi(editorId, 'getDocument')?.defaultView ||
                                        window;
                                    showCustomColorPicker(
                                        lightModeColor,
                                        color => {
                                            callFormatApi(editorId, 'setTextColor', [color]);
                                            lazySetLastTextColor.importAndExecute(
                                                formatViewState,
                                                color
                                            );
                                        },
                                        targetWindow
                                    );
                                },
                                tabId && isFeatureEnabled('mon-ribbon-keytips')
                                    ? constructKeytip(
                                          [
                                              getKeytipString(tabId),
                                              getKeytipString(FormatControlId.FontColorPicker),
                                          ],
                                          getKeytipString(FormatControlId.MoreColor)
                                      )
                                    : undefined,
                                MoreColor
                            ),
                        ],
                    },
                ],
            },
            () => callFormatApi(editorId, 'setTextColor', [lastTextColors]),
            tabId && isFeatureEnabled('mon-ribbon-keytips')
                ? constructKeytip(
                      [getKeytipString(tabId)],
                      getKeytipString(FormatControlId.FontColorPicker),
                      true
                  )
                : undefined,
            TextColorPickerIcon,
            undefined /* iconColor */,
            {
                icon: {
                    'path:nth-child(2)': {
                        fill: lightModeColor,
                    },
                },
            },
            {
                onAfterMenuDismiss: () => {
                    stopLivePreview(editorId);
                },
            }
        );
    }
);
